import { AnalyticsFilters } from '@/components/analytics/AnalyticsFilters';
import { ConsumptionChart } from '@/components/analytics/ConsumptionChart';
import { UnusedLinesTab } from '@/components/analytics/UnusedLinesTab';
import { PageHeader } from '@/components/PageHeader';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { useAnalyticsOwner } from '@/hooks/useAnalyticsOwner';
import { useMobileLineUsage, useMobileLinesLight } from '@/hooks/useLineAnalytics';
import { useExportMobileLineUsage } from '@/hooks/useLineExports';
import { fadeUp, stagger } from '@/lib/animations';
import { AnalyticsFilters as AnalyticsFiltersType } from '@/types/mobile-line.dto';
import { motion } from 'framer-motion';
import { BarChart3, PhoneOff } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useSearchParams } from 'react-router-dom';

function getDefaultDateRange(): DateRange {
  const today = new Date();
  return {
    from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
    to: new Date(today.getFullYear(), today.getMonth(), 0),
  };
}

export function AnalyticsView() {
  const [searchParams] = useSearchParams();
  const initialLineId = searchParams.get('lineId') ?? undefined;

  const { organizationId, siteId } = useAnalyticsOwner();

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);
  const [productType, setProductType] = useState<'data' | 'voice' | 'sms-mms'>('voice');
  const [selectedLineId, setSelectedLineId] = useState<string | undefined>(initialLineId);

  const { data: lines = [] } = useMobileLinesLight(organizationId, siteId);

  // Derive the effective lineId: explicit selection, or first line as fallback
  const effectiveLineId = useMemo(
    () => selectedLineId ?? lines[0]?.id,
    [selectedLineId, lines],
  );

  // Build API filters from state
  const filters = useMemo<AnalyticsFiltersType>(() => ({
    lineId: effectiveLineId,
    productType,
    startMonth: dateRange.from ? dateRange.from.getMonth() + 1 : undefined,
    startYear: dateRange.from ? dateRange.from.getFullYear() : undefined,
    startDay: dateRange.from ? dateRange.from.getDate() : undefined,
    endMonth: dateRange.to ? dateRange.to.getMonth() + 1 : undefined,
    endYear: dateRange.to ? dateRange.to.getFullYear() : undefined,
    endDay: dateRange.to ? dateRange.to.getDate() : undefined,
  }), [effectiveLineId, productType, dateRange]);

  const {
    data: usageResponse,
    isLoading,
    error,
  } = useMobileLineUsage(effectiveLineId, filters);

  const { mutate: exportUsage, isPending: isExporting } = useExportMobileLineUsage(organizationId);

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Analyse"
          subtitle="Visualisez l'historique de consommation de vos lignes mobiles"
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <Tabs defaultValue="consumption" className="space-y-4">
          <TabsList className="bg-white shadow-sm rounded-xl h-12 p-1 w-full sm:w-auto">
            <TabsTrigger
              value="consumption"
              className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              Analyse de la consommation
            </TabsTrigger>
            <TabsTrigger
              value="unused"
              className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none gap-2"
            >
              <PhoneOff className="h-4 w-4" />
              Lignes inutilisées
            </TabsTrigger>
          </TabsList>

          <TabsContent value="consumption">
            <div className="space-y-6">
              <AnalyticsFilters
                lines={lines}
                selectedLineId={effectiveLineId}
                onLineChange={setSelectedLineId}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                productType={productType}
                onProductTypeChange={setProductType}
                isLoading={isLoading}
              />
              <div>
                {error && (
                  <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-2xl">
                    <p className="text-destructive text-sm">
                      Erreur lors du chargement des données. Veuillez réessayer.
                    </p>
                  </div>
                )}
                <ConsumptionChart
                  data={usageResponse}
                  isLoading={isLoading}
                  productType={productType}
                  isExporting={isExporting}
                  onExport={(format) => {
                    if (!effectiveLineId) return;
                    exportUsage({ lineId: effectiveLineId, filters, format });
                  }}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="unused">
            <UnusedLinesTab organizationId={organizationId} siteId={siteId} />
          </TabsContent>
        </Tabs>
      </motion.div>
    </motion.div>
  );
}
